<template>
  <div class="linked-ads">
    <!-- The deliverable form renders this inside a collapsible section that
         already carries the "Linked Ads" heading and its own separator, so
         both are opt-out rather than baked in. -->
    <label v-if="showLabel">Linked Ads</label>

    <!-- Empty state: no Facebook/Meta token on this tenant at all. Showing
    the normal "Link Ad" affordance here would send the user into a picker
    that can only ever say "no ad accounts found" — better to surface the
    prerequisite up front, before they click anything. -->
    <div v-if="!metaAdsConnected" class="ui segment linked-ads-empty-state">
      <i class="icon facebook large" />
      <p>
        Connect a Meta Ads account to track boosted post performance and ad
        spend on this deliverable.
      </p>
      <a href="/tokens" target="_blank" class="ui small button">
        Connect Meta Ads Account
      </a>
    </div>

    <template v-else>
      <div v-if="linkedAds.length === 0" class="ui small message">
        <p>No ads linked to this deliverable.</p>
      </div>

      <div class="ui divided relaxed list">
        <div v-for="ad in linkedAds" :key="ad.ad_id" class="item" style="padding: 10px 6px;">
          <div class="right floated content" style="margin-top: 4px;">
            <button
              class="ui mini basic red button"
              @click="unlinkAd(ad)"
            >
              <i class="icon unlink" /> Unlink
            </button>
          </div>
          <img
            v-if="ad.thumbnail_url"
            class="ui mini image"
            :src="ad.thumbnail_url"
            loading="lazy"
            style="float: left; margin-right: 10px; border-radius: 4px; width: 48px; height: 48px; object-fit: cover;"
          />
          <div class="content">
            <div class="header">{{ ad.ad_name || ad.ad_id }}</div>
            <div class="description">
              <span class="ui text grey small">
                <span v-if="ad.campaign_name">{{ ad.campaign_name }}</span>
                <span v-if="ad.spend"> &mdash; ${{ ad.spend }} spent</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="margin-top-small">
        <a href="#" class="ui" @click.prevent="openPicker">
          <i class="icon linkify fitted" />
          {{ linkedAds.length > 0 ? 'Link Another Ad' : 'Link Ad' }}
        </a>
        <a v-if="linkedAds.length > 0 && !clearingAll" href="#" class="ui text red margin-left-medium" @click.prevent="unlinkAll">
          Clear All
        </a>
        <span v-if="clearingAll" class="margin-left-medium">
          <i class="ui mini active inline loader" /> Clearing...
        </span>
      </div>
    </template>

    <div v-if="showLabel" class="ui divider margin-y-medium" />

    <!-- Ad Picker Modal -->
    <div ref="modal" class="ui modal">
      <div class="header">
        <i class="icon facebook" /> Link an Ad to this Deliverable
      </div>
      <div class="content" style="min-height: 300px; max-height: 70vh; overflow-y: auto;">
        <div v-if="loadingAccounts" class="ui active centered inline loader margin-top-large"></div>

        <template v-else-if="adAccounts.length > 0">
          <div class="ui form margin-bottom-medium">
            <div class="field">
              <label>Ad Account</label>
              <select v-model="selectedAccountId" class="ui dropdown" @change="onAccountChange">
                <option v-for="acct in adAccounts" :key="acct.id" :value="acct.id">
                  {{ acct.name || acct.id }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="loadingAds" class="ui active centered inline loader"></div>

          <div v-else-if="fetchError" class="ui negative message">
            <p>{{ fetchError }}</p>
          </div>

          <!-- Only treat an empty list as "account has no ads" when there's no
               search query — search is server-side now, so zero results must
               keep the search box visible for the user to edit. -->
          <div v-else-if="availableAds.length === 0 && !searchQuery" class="ui message">
            <p>No ads found in this ad account.</p>
          </div>

          <template v-else>
            <div class="flex flex-row" style="gap: 8px;">
              <div class="ui icon input fluid">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search ads by name, campaign, or ad ID..."
                  ref="searchInput"
                />
                <i class="search icon" />
              </div>
              <select v-model="sortBy" class="ui compact dropdown" @change="resetAndFetchAds">
                <option value="recent">Newest</option>
                <option value="spend">Top spend</option>
                <option value="name">Name</option>
              </select>
            </div>
            <div class="margin-bottom-medium margin-top-tiny">
              <span v-if="adsLoadingStatus === 'loading'" class="ui text grey small">
                <i class="ui mini active inline loader" />
                Fetching ads from Meta&hellip; ({{ adsTotal }} so far)
              </span>
              <span v-else class="ui text grey small">
                <i class="icon clock outline" />
                {{ adsTotal }} ads<template v-if="syncedAgo"> &middot; synced {{ syncedAgo }}</template>
              </span>
              <a v-if="adsLoadingStatus !== 'loading'" href="#" class="ui text small margin-left-small" @click.prevent="fetchAvailableAds(true)">
                <i class="icon refresh" /> Refresh from Meta
              </a>
            </div>

          <div v-if="filteredAds.length === 0" class="ui message">
            <p>No ads match "{{ searchQuery }}"</p>
          </div>

          <div v-for="(ads, campaignName) in groupedAds" :key="campaignName" class="margin-bottom-medium">
            <h5 class="ui header grey">
              <i class="icon bullhorn small" />
              {{ campaignName || 'No Campaign' }}
            </h5>
            <div class="ui divided relaxed list">
              <div
                v-for="ad in ads"
                :key="ad.ad_id"
                class="item"
                style="padding: 10px 6px; cursor: pointer;"
                @click="toggleCheck(ad)"
              >
                <div class="right floated content" style="margin-top: 10px;">
                  <span v-if="alreadyLinked(ad.ad_id)" class="ui green text small">
                    <i class="icon check circle" /> Linked
                  </span>
                  <span v-else-if="ad.spend > 0" class="ui text grey small">
                    ${{ ad.spend }}
                  </span>
                </div>
                <div class="ui checkbox" style="float: left; margin-right: 10px; margin-top: 10px;" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isChecked(ad.ad_id)"
                    :disabled="alreadyLinked(ad.ad_id)"
                    @change="toggleCheck(ad)"
                  />
                  <label></label>
                </div>
                <img
                  v-if="ad.thumbnail_url"
                  class="ui mini image"
                  :src="ad.thumbnail_url"
                  loading="lazy"
                  style="float: left; margin-right: 10px; border-radius: 4px; width: 48px; height: 48px; object-fit: cover;"
                />
                <div class="content">
                  <div class="header">{{ ad.ad_name }}</div>
                  <div class="description">
                    <span class="ui text grey small">
                      <span v-if="ad.status">{{ ad.status }}</span>
                      <span v-if="ad.spend > 0"> &mdash; ${{ ad.spend }} spent</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            v-if="canLoadMore"
            class="ui basic fluid button"
            :class="{ loading: loadingMore }"
            :disabled="loadingMore"
            @click="loadMoreAds"
          >
            Load more ({{ adsTotal - availableAds.length }} remaining)
          </button>
          </template>
        </template>

        <!-- A token that was never granted ads_read is the one case where
             sending the user to re-auth IS the fix, so it gets its own branch
             above the generic error. -->
        <div v-else-if="authorizeUrl" class="ui warning message">
          <p>InfluenceKit needs permission to read your Meta ad accounts.</p>
          <a :href="authorizeUrl" class="ui pink button" data-turbo="false">
            <i class="icon facebook" />
            Grant ads access
          </a>
        </div>

        <!-- fetchError must be checked before the empty state: the ad_accounts
             request failing is not the same as the user having no ad accounts,
             and showing the "connect with ads_read" copy for an API error sends
             users off to reconnect a perfectly good token. -->
        <div v-else-if="fetchError" class="ui negative message">
          <p>{{ fetchError }}</p>
        </div>

        <div v-else class="ui message">
          <p>No ad accounts found. Connect a Facebook account with ads_read permission first.</p>
        </div>
      </div>
      <div class="actions">
        <button
          v-if="checkedAdIds.length > 0"
          class="ui primary button"
          :class="{ loading: linkingBatch }"
          :disabled="linkingBatch"
          @click="linkSelected"
        >
          <i class="icon linkify" />
          Link {{ checkedAdIds.length }} Ad{{ checkedAdIds.length > 1 ? 's' : '' }}
        </button>
        <div class="ui cancel button">Close</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    deliverableId: {
      type: Number,
      required: true,
    },
    initialLinkedAds: {
      type: Array,
      default: () => [],
    },
    metaAdsConnected: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      linkedAds: [...this.initialLinkedAds],
      adAccounts: [],
      selectedAccountId: null,
      loadingAccounts: false,
      availableAds: [],
      loadingAds: false,
      linkingAdId: null,
      unlinkingAdId: null,
      clearingAll: false,
      checkedAdIds: [],
      linkingBatch: false,
      fetchError: null,
      authorizeUrl: null,
      searchQuery: "",
      sortBy: "recent",
      adsTotal: 0,
      adsPage: 1,
      adsSyncedAt: null,
      loadingMore: false,
      searchDebounceTimer: null,
      adsLoadingStatus: null,
      adsPollTimer: null,
    };
  },
  computed: {
    // Search and sort are server-side now (the backend pages through Postgres),
    // so the loaded list is already filtered.
    filteredAds() {
      return this.availableAds;
    },
    groupedAds() {
      const groups = {};
      this.filteredAds.forEach((ad) => {
        const key = ad.campaign_name || "Ungrouped";
        if (!groups[key]) groups[key] = [];
        groups[key].push(ad);
      });
      return groups;
    },
    canLoadMore() {
      return this.adsLoadingStatus === "ready" && this.availableAds.length < this.adsTotal;
    },
    syncedAgo() {
      if (!this.adsSyncedAt) return null;
      const mins = Math.max(0, Math.round((Date.now() - new Date(this.adsSyncedAt)) / 60000));
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hours = Math.round(mins / 60);
      return hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
    },
  },
  watch: {
    searchQuery() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => this.resetAndFetchAds(), 300);
    },
  },
  beforeUnmount() {
    this.cancelAdsPoll();
  },
  methods: {
    alreadyLinked(adId) {
      return this.linkedAds.some((a) => a.ad_id === adId);
    },
    isChecked(adId) {
      return this.checkedAdIds.includes(adId);
    },
    toggleCheck(ad) {
      if (this.alreadyLinked(ad.ad_id)) return;
      const idx = this.checkedAdIds.indexOf(ad.ad_id);
      if (idx >= 0) {
        this.checkedAdIds.splice(idx, 1);
      } else {
        this.checkedAdIds.push(ad.ad_id);
      }
    },
    linkSelected() {
      if (this.checkedAdIds.length === 0 || this.linkingBatch) return;
      this.linkingBatch = true;

      $.ajax({
        url: `/deliverables/${this.deliverableId}/link_ads`,
        type: "POST",
        dataType: "json",
        data: { ad_account_id: this.selectedAccountId, ad_ids: this.checkedAdIds },
        headers: {
          "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content"),
        },
        success: (data) => {
          data.ad_metrics.forEach((metric) => {
            this.linkedAds.push({
              ad_id: metric.ad_id,
              ad_name: metric.ad_name,
              campaign_name: metric.campaign_name,
              spend: metric.spend,
              thumbnail_url: metric.thumbnail_url,
            });
          });
          this.checkedAdIds = [];
          this.$emit("changed");
          $(this.$refs.modal).modal("hide");
        },
        error: (xhr) => {
          const resp = xhr.responseJSON;
          alert((resp && resp.error) || "Failed to link ads");
        },
        complete: () => {
          this.linkingBatch = false;
        },
      });
    },
    openPicker() {
      this.searchQuery = "";
      $(this.$refs.modal).modal({
        onHide: () => {
          this.cancelAdsPoll();
          return true;
        },
      }).modal("show");
      if (this.adAccounts.length === 0) {
        // Don't wait for the accounts list before fetching ads: if we know the
        // last-used account, fire both requests in parallel and cut the cold
        // open from two serial network hops to one.
        const lastUsed = localStorage.getItem("meta_ads_last_account");
        if (lastUsed && !this.selectedAccountId) {
          this.selectedAccountId = lastUsed;
          this.fetchAvailableAds();
        }
        this.fetchAdAccounts();
      }
    },
    fetchAdAccounts() {
      this.loadingAccounts = true;
      this.authorizeUrl = null;
      $.ajax({
        url: `/deliverables/${this.deliverableId}/ad_accounts`,
        type: "GET",
        dataType: "json",
        success: (data) => {
          this.adAccounts = data;
          if (data.length > 0 && !data.some((a) => a.id === this.selectedAccountId)) {
            // No valid selection yet (or the optimistically-selected last-used
            // account no longer exists for this token) — pick one and fetch.
            this.selectedAccountId = data[0].id;
            this.fetchAvailableAds();
          }
        },
        error: (xhr) => {
          const resp = xhr.responseJSON;
          if (resp && resp.needs_ads_permission) {
            this.authorizeUrl = resp.authorize_url;
          }
          this.fetchError = (resp && resp.error) || "Failed to fetch ad accounts";
        },
        complete: () => {
          this.loadingAccounts = false;
        },
      });
    },
    onAccountChange() {
      localStorage.setItem("meta_ads_last_account", this.selectedAccountId);
      this.availableAds = [];
      this.searchQuery = "";
      this.sortBy = "recent";
      this.checkedAdIds = [];
      this.fetchAvailableAds();
    },
    resetAndFetchAds() {
      if (!this.selectedAccountId) return;
      this.fetchAvailableAds();
    },
    loadMoreAds() {
      if (!this.canLoadMore || this.loadingMore) return;
      this.loadingMore = true;
      this._fetchAvailableAdsRequest(false, this.adsPage + 1);
    },
    fetchAvailableAds(bustCache) {
      this.cancelAdsPoll();
      this.fetchError = null;
      // Only show the full-screen loader on the first fetch (no ads yet).
      // Subsequent polls keep the partial list visible while more stream in.
      if (this.availableAds.length === 0) this.loadingAds = true;
      this.adsLoadingStatus = "loading";
      this._fetchAvailableAdsRequest(!!bustCache, 1);
    },
    _fetchAvailableAdsRequest(bustCache, page) {
      const accountId = this.selectedAccountId;
      let url = `/deliverables/${this.deliverableId}/available_ads?ad_account_id=${accountId}&page=${page}&sort=${this.sortBy}`;
      if (this.searchQuery.trim()) url += `&q=${encodeURIComponent(this.searchQuery.trim())}`;
      if (bustCache) url += "&bust_cache=1";

      $.ajax({
        url: url,
        type: "GET",
        dataType: "json",
        success: (data) => {
          // Drop the response if the user switched accounts mid-flight.
          if (accountId !== this.selectedAccountId) return;
          this.adsPage = data.page || page;
          this.adsTotal = data.total || (data.ads || []).length;
          this.adsSyncedAt = data.synced_at || null;
          if (page > 1) {
            this.availableAds = this.availableAds.concat(data.ads || []);
          } else {
            this.availableAds = data.ads || [];
          }
          this.adsLoadingStatus = data.status || "ready";
          if (data.status === "error") {
            this.fetchError = data.error || "Failed to fetch ads";
            this.loadingAds = false;
            return;
          }
          if (data.status === "loading") {
            // Hide the full-screen loader once we have at least one ad to show.
            if (this.availableAds.length > 0) this.loadingAds = false;
            // While Meta is syncing, keep refreshing page 1 (rows stream in).
            this.adsPollTimer = setTimeout(() => this._fetchAvailableAdsRequest(false, 1), 2000);
          } else {
            this.loadingAds = false;
          }
        },
        error: (xhr) => {
          const resp = xhr.responseJSON;
          this.fetchError = (resp && resp.error) || "Failed to fetch ads";
          this.loadingAds = false;
        },
        complete: () => {
          this.loadingMore = false;
        },
      });
    },
    cancelAdsPoll() {
      if (this.adsPollTimer) {
        clearTimeout(this.adsPollTimer);
        this.adsPollTimer = null;
      }
    },
    linkAd(ad) {
      if (this.alreadyLinked(ad.ad_id) || this.linkingAdId) return;
      this.linkingAdId = ad.ad_id;

      $.ajax({
        url: `/deliverables/${this.deliverableId}/link_ad`,
        type: "POST",
        dataType: "json",
        data: { ad_id: ad.ad_id, ad_name: ad.ad_name, thumbnail_url: ad.thumbnail_url, ad_account_id: this.selectedAccountId },
        headers: {
          "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content"),
        },
        success: (data) => {
          this.linkedAds.push({
            ad_id: ad.ad_id,
            ad_name: ad.ad_name,
            campaign_name: data.ad_metric.campaign_name,
            spend: data.ad_metric.spend,
            thumbnail_url: ad.thumbnail_url,
          });
          this.$emit("changed");
        },
        error: (xhr) => {
          const resp = xhr.responseJSON;
          alert((resp && resp.error) || "Failed to link ad");
        },
        complete: () => {
          this.linkingAdId = null;
        },
      });
    },
    doUnlinkAd(ad) {
      this.unlinkingAdId = ad.ad_id;
      $.ajax({
        url: `/deliverables/${this.deliverableId}/unlink_ad`,
        type: "DELETE",
        dataType: "json",
        data: { ad_id: ad.ad_id },
        headers: {
          "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content"),
        },
        success: () => {
          this.linkedAds = this.linkedAds.filter((a) => a.ad_id !== ad.ad_id);
          this.$emit("changed");
        },
        complete: () => {
          this.unlinkingAdId = null;
        },
      });
    },
    unlinkAd(ad) {
      if (!confirm(`Unlink "${ad.ad_name || ad.ad_id}"?`)) return;
      this.doUnlinkAd(ad);
    },
    unlinkAdFromModal(ad) {
      this.doUnlinkAd(ad);
    },
    unlinkAll() {
      if (!confirm("Remove all linked ads from this deliverable?")) return;
      this.clearingAll = true;

      $.ajax({
        url: `/deliverables/${this.deliverableId}/unlink_all_ads`,
        type: "DELETE",
        dataType: "json",
        headers: {
          "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content"),
        },
        success: () => {
          this.linkedAds = [];
          this.$emit("changed");
        },
        complete: () => {
          this.clearingAll = false;
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.linked-ads-empty-state {
  text-align: center;
  padding: 1.5rem 1rem !important;
  background: var(--bg-surface, #fafafa) !important;

  .icon {
    color: #1877f2; // Meta/Facebook blue
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-secondary, #4a5568);
    font-size: 0.88rem;
    max-width: 26rem;
    margin: 0 auto 0.9rem;
  }
}
</style>
